<template>
  <div id="app" :key="isLoading" class="h-100">
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      :is-full-page="true"
      :opacity="1"
      :z-index="1200"
    >
      <slot name="default">
        <div class="text-center">
          <img :src="icoolLogo" />
        </div>
      </slot>
      <slot name="before">
        <div class="d-flex justify-content-center">
          <loader />
        </div>
        <div class="text-center">
          <h2 v-if="snapshotLoadingReason">{{ snapshotLoadingReason }}</h2>
          {{ $t('loading') }}
        </div>
      </slot>
    </loading>
    <top-navigation @menu-button-clicked="toggleMenuOpen" />
    <sidebar v-model="menuOpen" header-class="main-menu-header" lazy>
      <main-side-bar-menu />
      <template v-slot:title>
        <icool-icon icon="home-lg" @click.native="goToStartingScreen" class="cursor-pointer" />
      </template>
    </sidebar>
    <div class="d-flex align-content-stretch h-100" :style="{ 'padding-top': '4rem' }">
      <main-side-bar-menu class="d-none d-lg-block" />
      <main-content />
    </div>
    <scene-f-a-b />
    <slideout-panel></slideout-panel>
  </div>
</template>


<script>
import axios from 'axios';
import Vue from 'vue';
import store from './store';
import VueSocketIO from 'vue-socket.io';
import socket from 'socket.io-client';
import { loadLanguageAsync } from './i18n-setup';
import TopNavigation from '@/components/navigation/top-navigation/TopNavigation.vue';
import Loading from 'vue-loading-overlay';
import MainSideBarMenu from '@/components/navigation/sidebar-menu/MainSideBarMenu.vue';
import {
  mapGetters, mapActions, mapState, mapMutations,
} from 'vuex';
import config from '@/config';
import IcoolLogo from '@/assets/icool-white.png';
import Loader from '@/components/utils/Loader.vue';
import eventBus from '@/components/utils/slideout-panel/eventBus';
import Login from '@/mixins/Login';
import Sidebar from '@/components/utils/Sidebar';
import MainContent from '@/components/layout/MainContent';
import * as types from '@/store/mutation-types';
import I18N from "@/mixins/I18N";
import SceneFAB from "@/components/scenes/SceneFAB";
import PushNotifications from '@/mixins/PushNotifications';

export default {
  name: 'App',
  mixins: [Login, I18N, PushNotifications],
  data() {
    return {
      device: null, // device is set only within cordova app
      isLoading: true,
      snapshotLoadingReason: null,
      menuOpen: false,
      menuCollapsed: false,
      icoolLogo: IcoolLogo,
      dataIsRefreshing: false,
    };
  },
  computed: {
    ...mapState('snapshot', [
      'currentSnapshot',
    ]),
    ...mapGetters('user', [
      'getUserData',
    ]),
    ...mapGetters([
      'getDefaultSpacePlan',
      'getFavoriteServices',
      'getPlaces',
    ]),
    ...mapGetters('app', [
      'isConnectionOk',
    ]),
    canStoreNewDevice() {
      return !this.isLoading && Boolean(this.pushRegistrationId);
    },
  },
  methods: {
    ...mapMutations('app', {
      setCloudStatus: types.SET_CLOUD_STATUS,
      setOnlineStatus: types.SET_ONLINE_STATUS,
    }),
    ...mapMutations('snapshot', {
      setCurrentSnapshot: types.SET_CURRENT_SNAPSHOT,
    }),
    ...mapActions([
      'loadPlaces',
      'loadServices',
      'loadActors',
      'loadVars',
      'loadScenes',
      'loadScenesVars',
      'loadSpaceplans',
    ]),
    ...mapActions('user', [
      'loadUserData',
    ]),
    ...mapActions('service-group', [
      'loadServiceGroups',
    ]),
    ...mapActions('notifications', [
      'loadNotifications',
    ]),
    ...mapActions('snapshot', [
      'loadSnapshots',
      'setFirstAvailableSnapshotAsCurrent',
      'loadSnapshotData',
    ]),
    ...mapActions('paid-subscription', [
      'loadPaidSubscription',
    ]),
    onCollapse(collapsed) {
      this.menuCollapsed = collapsed;
    },
    toggleMenuOpen() {
      this.menuOpen = !this.menuOpen;
    },
    createAxiosResponseInterceptor() {
      axios.interceptors.response.use(
        response => response,
        (error) => {
          if (error.response.status === 403) { // it should be case for expired access token
            this.goToLogin();
          }
          return Promise.reject(error);
        },
      );
    },
    reloadWebSocketConnection() {
      this.setCloudStatus({ status_ws: false });
      if (!config.ICOOL.ENABLE_WEBSOCKETS) {
        this.$log.info('WebSockets are disabled by configuration');
        return;
      }
      this.$log.info('reloading WebSocket connection for snapshot id', this.currentSnapshot.id);
      if (this.$socket) {
        this.$socket.off('reconnect');
        this.$socket.off('connect_error');
        if (this.$socket.connected) {
          this.$log.info('closing WebSocket connection');
          this.$socket.close();
        }
      }
      if (!this.$socket) {
        this.$log.info('openning new WebSocket connection');
        Vue.use(new VueSocketIO({
          debug: config.ICOOL.WEBSOCKET_DEBUG,
          connection: socket.connect(config.ICOOL.WEBSOCKET_SERVER, {
            query: this._getWSConnectionQuery(),
          }),
          vuex: {
            store,
            actionPrefix: 'SOCKET_',
            mutationPrefix: 'SOCKET_',
          },
        }));
      } else {
        this.$log.info('reopenning WebSocket connection');
        this.$socket.io.opts.query = this._getWSConnectionQuery();
        this.$socket.connect();
      }
      this.setCloudStatus({ status_ws: true });
      this.$socket.on('reconnect', this.webSocketReconnected);
      this.$socket.on('connect_error', this.webSocketError);
    },
    webSocketReconnected() {
      this.$log.info('websocket connection reconnected');
      this.updateCurrentSnapshot();
      this.setCloudStatus({ status_ws: true });
    },
    webSocketError() {
      this.$log.info('websocket connection error');
      this.setCloudStatus({ status_ws: false });
    },
    async updateCurrentSnapshot() {
      const url = this.$backendUrl(config.ICOOL.API.SNAPSHOT_PATH(this.currentSnapshot.id));
      try {
        const response = await axios.get(url);
        this.setCurrentSnapshot(response.data);
      } catch (err) {
        this.$log.error(err);
        return;
      }
      this.updateCloudStatus();
    },
    _getWSConnectionQuery() {
      return `token=Bearer ${this.accessToken}&snapshotId=${this.currentSnapshot.id}&userId=${this.getUserData.user.id}`;
    },
    async initSnapshots() {
      try {
        await this.loadSnapshots();
        this.setFirstAvailableSnapshotAsCurrent();
        await this.initSnapshot();
      } catch (e) {
        this.$log.error('Loading snapshots failed', e);
      }
    },
    async initSnapshot() {
      this.isLoading = true;
      await Promise.all([
        this.loadUserData(),
        this.loadPlaces(),
        this.loadServiceGroups(),
        this.loadServices(),
        this.loadActors(),
        this.loadVars(),
        this.loadScenes(),
        this.loadScenesVars(),
        this.loadNotifications(),
        this.loadSpaceplans(),
        this.loadPaidSubscription(),
      ]);
      loadLanguageAsync(this.selectedLanguage).then(() => { this.isLoading = false; });
      this.reloadWebSocketConnection();
      this.goToStartingScreen();
      this.snapshotLoadingReason = null;
      this.updateCloudStatus();
    },
    goToStartingScreen() {
      if (this.getUserData.starting_screen === 'visualization') {
        const spaceplan = this.getDefaultSpacePlan;
        if (spaceplan) {
          this.$router.push({ name: 'spaceplan', params: { id: spaceplan.id } });
        } else {
          this.$router.push({ name: 'spaceplans' });
        }
      } else {
        if (this.getFavoriteServices.length === 0 && this.getPlaces.length > 0) {
          this.$router.push({ name: 'service-groups', params: { placeId: this.getPlaces[0].id } });
        } else {
          this.$router.push({ name: 'dashboard' });
        }
      }
    },
    updateLanguage(languageId) {
      loadLanguageAsync(config.LANGUAGES[languageId]).then(() => {
        this.initSnapshot();
      });
    },
    updateVars() {
      if (!this.dataIsRefreshing) {
        this.dataIsRefreshing = true;
        setTimeout(() => { // prevent multiple times data refreshing at same time
          this.dataIsRefreshing = false;
        }, 5000);
        this.$log.info('Screen is activated. Refreshing data');
        if (!config.ICOOL.DISABLE_UPDATING_VARS) {
          this.loadSnapshotData();
        } else {
          this.$log.info('Updating variables disabled by config');
        }
      }
    },
    updateCloudStatus() {
      const cloudStatus = {
        status_plc: this.currentSnapshot.status_plc,
        status_vpn: this.currentSnapshot.status_vpn,
        status_coms: this.currentSnapshot.status_coms,
      };
      this.setCloudStatus(cloudStatus);
    },
    updateOnlineStatus() {
      this.setOnlineStatus(navigator.onLine);
    },
    onDeviceReady() {
      this.$log.info('device ready', device);
      this.device = device;
      this.initPushNotifications();
      this.goToLogin();
    },
    configureAccessToken(accessToken) {
      axios.interceptors.request.use((options) => {
        options.headers.Authorization = `Bearer ${accessToken}`;
        options.responseType = 'json';
        return options;
      }, err => Promise.reject(err));
    },
  },
  beforeCreate() {
  },
  mounted() {
    this.createAxiosResponseInterceptor();
    if (!config.CORDOVA) { // without cordova we can´t wait on deviceready event
      this.fetchAccessToken();
    }
  },
  watch: {
    currentSnapshot(newVal, oldVal) {
      this.$log.info('current snapshot changed', newVal, oldVal);
      if (oldVal !== null && newVal.id !== oldVal.id) {
        this.initSnapshot();
      }
      // new snapshot version is here
      if (oldVal !== null && newVal.project === oldVal.project && newVal.id !== oldVal.id) {
        this.snapshotLoadingReason = this.$t('new-project-version');
        this.initSnapshot();
      }
    },
    isConnectionOk(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.$log.info('cloud connection lost');
      }
      if (newVal && !oldVal) {
        this.$log.info('cloud connection is up');
        this.loadSnapshotData();
      }
    },
    device() {
      if (this.device) {
        console.log(this.device);
      }
    },
    accessToken() {
      if (this.accessToken) {
        this.initSnapshots();
      }
    },
    canStoreNewDevice(newValue) {
      this.$log.debug('canStoreNewDevice changed', newValue);
      if (newValue) {
        this.storeNewDevice(this.pushRegistrationId);
      }
    },
  },
  created() {
    eventBus.$on('goToStartingScreen', this.goToStartingScreen);
    eventBus.$on('languageChanged', this.updateLanguage);
    eventBus.$on('refreshSnapshot', this.initSnapshot);
    document.addEventListener('visibilitychange', this.updateVars);
    document.addEventListener('resume', this.updateVars); // comes from Cordova
    window.addEventListener('online', this.updateOnlineStatus);
    window.addEventListener('offline', this.updateOnlineStatus);
    document.addEventListener('deviceready', this.onDeviceReady, false);
  },
  destroyed() {
    eventBus.$off('languageChanged');
    eventBus.$off('goToStartingScreen');
    eventBus.$off('refreshSnapshot');
    document.removeEventListener('visibilitychange', this.updateVars);
    document.removeEventListener('resume', this.updateVars);
    window.removeEventListener('online', this.updateOnlineStatus);
    window.removeEventListener('offline', this.updateOnlineStatus);
    document.removeEventListener('deviceready', this.onDeviceReady);
  },
  components: {
    SceneFAB,
    MainContent,
    Sidebar,
    Loader,
    TopNavigation,
    MainSideBarMenu,
    Loading,
  },
};
</script>


<style lang="scss">
  @import './assets/scss/app.scss';

  /* Enter and leave animations can use different */
  /* durations and timing functions.              */
  .slide-fade-enter-active {
    transition: all .3s ease;
  }
  .slide-fade-enter
  /* .slide-fade-leave-active below version 2.1.8 */ {
    transform: translateX(10px);
    opacity: 0;
  }
  .icool-logo {
    position: fixed;
    bottom: 10px;
  }
</style>
